import { Database, Image, Type } from "lucide-react";
import type { MapStyleDependencySummaryProps } from "../../contracts/map-styles";
import { cn } from "../../lib/utils";

export function MapStyleDependencySummary({
  dependencies,
  compact = false,
  emptyMessage = "No dependencies identified",
  className,
}: MapStyleDependencySummaryProps) {
  const hasSources = dependencies.sources.length > 0;
  const hasSprite = !!dependencies.sprite;
  const hasGlyph = !!dependencies.glyph;
  const hasAnything = hasSources || hasSprite || hasGlyph;

  if (!hasAnything) {
    return (
      <div className={cn("text-[10px] font-medium text-muted-foreground/60 italic", className)}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-start gap-x-4 gap-y-2", className)}>
      {/* Sources */}
      {hasSources && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {!compact && (
            <div className="flex h-5 w-5 items-center justify-center rounded bg-primary/10 text-primary">
              <Database className="h-3 w-3" />
            </div>
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {dependencies.sources.map((source) => (
              <div
                key={source.id}
                title={source.description || source.label}
                className={cn(
                  "flex max-w-full items-center gap-1.5 rounded-md border border-border/60 bg-background/50 px-1.5 py-0.5 transition-colors hover:border-border",
                  compact ? "text-[9px]" : "text-[10px]"
                )}
              >
                <span className="max-w-[9rem] truncate font-black text-foreground sm:max-w-none">
                  {source.alias || source.id}
                </span>
                {source.alias && (
                  <span className="max-w-[11rem] truncate text-muted-foreground/60 font-medium sm:max-w-none">
                    ({source.label})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assets (Sprite/Glyph) */}
      {(hasSprite || hasGlyph) && (
        <div className="flex w-full flex-wrap items-center gap-3 border-t border-border/40 pt-2 sm:w-auto sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0">
          {dependencies.sprite && (
            <div 
              title={`Sprite: ${dependencies.sprite.label}`}
              className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground hover:text-foreground transition-colors"
            >
              <Image className="h-3 w-3 text-primary/70" />
              {compact ? "Sprite" : dependencies.sprite.label}
            </div>
          )}
          {dependencies.glyph && (
            <div 
              title={`Glyphs: ${dependencies.glyph.label}`}
              className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground hover:text-foreground transition-colors"
            >
              <Type className="h-3 w-3 text-muted-foreground/80" />
              {compact ? "Glyphs" : dependencies.glyph.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
